import React from "react";
import { gbp, fmtDate, STATE_STYLE } from "./bookingFormat";
import { FollowUpControl } from "./FollowUpControl";
import { SeenToggle } from "./SeenToggle";
import { ReceiptActions } from "./ReceiptActions";
import { ResultsReadyButton } from "./ResultsReadyButton";

const StateBadge = ({ row }) => {
  const s = STATE_STYLE[row.state] || STATE_STYLE.released;
  return (
    <span className="inline-flex flex-col gap-1">
      <span className={`inline-block w-fit text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
      {row.needs_manual_booking && <span data-testid={`booking-not-in-calendly-${row.id}`} className="text-[11px] text-red-700 font-medium">Not in Calendly – book manually or refund</span>}
      {row.patient_cancelled && <span className="text-[11px] text-red-700">Patient cancelled in Calendly – refund due?</span>}
      {row.cancelled_by && <span className="text-[11px] text-ink-muted">by {row.cancelled_by}</span>}
      {row.refund_failed && <span data-testid={`booking-refund-failed-${row.id}`} className="text-[11px] text-red-700 font-medium">Refund failed – refund in Stripe</span>}
    </span>
  );
};

const Check = ({ checked, onChange, testId, label }) => (
  <input type="checkbox" checked={checked} onChange={onChange} data-testid={testId} aria-label={label} className="accent-[#8C6643] w-4 h-4 cursor-pointer" />
);

const Row = ({ row, onCancel, onMove, onInvite, selected, onToggle }) => (
  <tr data-testid={`booking-row-${row.id}`} className={`border-t border-stone-200/60 align-top ${selected ? "bg-cream-100" : ""}`}>
    <td className="py-3 pr-3"><Check checked={selected} onChange={() => onToggle(row.id)} testId={`booking-select-${row.id}`} label="Select booking" /></td>
    <td className="py-3 pr-4"><StateBadge row={row} /></td>
    <td className="py-3 pr-4">
      <div className="text-ink">{row.patient_name || "—"}</div>
      <div className="text-xs text-ink-muted">{row.patient_email}</div>
      {row.booking_ref && <div data-testid={`booking-ref-${row.id}`} className="text-xs font-mono text-bronze-dark">{row.booking_ref}</div>}
      {row.patient_phone && <div className="text-xs text-ink-muted">{row.patient_phone}</div>}
      {row.visit_type && <div data-testid={`booking-visit-type-${row.id}`} className="mt-1 text-xs font-medium text-ink">{row.visit_type}</div>}
      {row.pre_slots?.map((p) => <div key={p.slug} data-testid={`booking-pre-${row.id}-${p.slug}`} className="text-xs text-ink-muted">{new Date(p.start_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })} · {p.label}</div>)}
      {row.reasons?.length > 0 && <div data-testid={`booking-reasons-${row.id}`} className="mt-1 text-xs text-bronze-dark">{row.reasons.join(" · ")}</div>}
      {(row.heard_about || row.visit_source) && (
        <div data-testid={`booking-source-${row.id}`} className="mt-1 text-xs text-ink-muted">
          {row.heard_about && <div>Heard via: <span className="text-ink">{row.heard_about}</span></div>}
          {row.visit_source && <div>Arrived from: <span className="text-ink">{row.visit_source}</span></div>}
        </div>
      )}
      {row.nhs_number && <div data-testid={`booking-nhs-${row.id}`} className="text-xs text-ink-muted">NHS {row.nhs_number.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3")}</div>}
    </td>
    <td className="py-3 pr-4 whitespace-nowrap">{fmtDate(row.start_time)}</td>
    <td data-testid={`booking-consultant-${row.id}`} className="py-3 pr-4 whitespace-nowrap">{row.consultant || "—"}</td>
    <td className="py-3 pr-4 whitespace-nowrap text-ink-muted">{fmtDate(row.created_at)}</td>
    <td className="py-3 pr-4 whitespace-nowrap">
      {row.amount ? gbp(row.amount) : "—"}
      {row.refunded_amount > 0 && <div className="text-xs text-red-700">−{gbp(row.refunded_amount)} refunded</div>}
    </td>
    <td className="py-3 pr-4">{row.state === "paid" && <div className="flex flex-col items-start gap-1.5"><SeenToggle email={row.patient_email} testId={`seen-toggle-${row.id}`} invited={row.followup_invite === "sent"} /><FollowUpControl row={row} /></div>}</td>
    <td className="py-3 text-right space-y-1.5">
      {row.state === "paid" && row.start_time && (
        <button type="button" onClick={() => onInvite(row)} data-testid={`booking-consultant-invite-${row.id}`}
          className="block ml-auto text-sm text-bronze-dark hover:text-bronze underline underline-offset-4 whitespace-nowrap">Send invite to consultant</button>
      )}
      {row.state === "paid" && row.patient_email && <ResultsReadyButton row={row} />}
      {row.state === "paid" && row.movable && (
        <button type="button" onClick={() => onMove(row)} data-testid={`booking-move-${row.id}`}
          className="block ml-auto text-sm text-bronze-dark hover:text-bronze underline underline-offset-4 whitespace-nowrap">Change time</button>
      )}
      {row.can_cancel && (
        <button type="button" onClick={() => onCancel(row)} data-testid={`booking-cancel-refund-${row.id}`}
          className="text-sm text-red-700 hover:text-red-900 underline underline-offset-4 whitespace-nowrap">
          Cancel & refund
        </button>
      )}
      {(row.state === "paid" || row.refunded_amount > 0) && row.amount > 0 && <ReceiptActions kind="booking" id={row.id} className="text-sm justify-end w-full" />}
    </td>
  </tr>
);

export const BookingsTable = ({ rows, onCancel, onMove, onInvite, selected, onToggle, onToggleAll }) => {
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.id));
  if (!rows.length) {
    return <div data-testid="bookings-empty" className="mt-6 p-6 bg-cream-100 border border-bronze-light/40 rounded-md text-sm text-ink-muted">No bookings here yet.</div>;
  }
  return (
    <div className="mt-6 overflow-x-auto bg-white border border-stone-200/70 rounded-md px-5">
      <table data-testid="bookings-table" className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-ink-muted">
            <th className="py-3 pr-3"><Check checked={allOn} onChange={() => onToggleAll(!allOn)} testId="bookings-select-all" label="Select all shown" /></th>
            <th className="py-3 pr-4 font-medium">Status</th>
            <th className="py-3 pr-4 font-medium">Patient</th>
            <th className="py-3 pr-4 font-medium">Appointment</th>
            <th className="py-3 pr-4 font-medium">Consultant</th>
            <th className="py-3 pr-4 font-medium">Booked</th>
            <th className="py-3 pr-4 font-medium">Amount</th>
            <th className="py-3 pr-4 font-medium">Follow-up invite</th>
            <th className="py-3" />
          </tr>
        </thead>
        <tbody>{rows.map((r) => <Row key={r.id} row={r} onCancel={onCancel} onMove={onMove} onInvite={onInvite} selected={selected.has(r.id)} onToggle={onToggle} />)}</tbody>
      </table>
    </div>
  );
};
